/* @flow */
import {edapTriggerPriceSummaryDisplay} from '@homeaway/react-price-summary';
import {trigger as edapTrigger} from './edapWrapper';
import type {ListingShape as MapHitListingShape} from '../components/MapHit/MapHit';
import type {ListingShape as HitListingShape} from '../components/Hit/Hit';
import type {Badge} from '../components/HitInfo/HitInfo';
import {type MultiUnitItemShape} from '../components/MultiUnitItem/MultiUnitItem';

export const LIST_HIT_CLICK = 'search.hit.click';
export const MAP_HIT_CLICK = 'search.map.hit.click';
export const MAP_MARKER_CLICK = 'search.map.property.click';
export const MULTIUNIT_ITEM_CLICK = 'multiunit.item.click';
export const MULTI_UNIT_HIT_CLICK = 'multiunit.hit.click';
export const MULTI_UNIT_HIT_EXPOSED = 'multiunit.hit.exposed';
export const MULTI_UNIT_BUTTON_CLICK = 'multiunit.button.click';
export const MULTI_UNIT_RESULT_COUNT = 'multiunit.result.count';
export const SEARCH_RESULT_SET_SELECTED = 'search_result_set.selected';

type HitEdapEventAction =
    | typeof LIST_HIT_CLICK
    | typeof MAP_HIT_CLICK
    | typeof MAP_MARKER_CLICK
    | typeof MULTI_UNIT_HIT_CLICK
    | typeof MULTI_UNIT_HIT_EXPOSED
    | typeof MULTI_UNIT_BUTTON_CLICK
    | typeof MULTI_UNIT_RESULT_COUNT
    | typeof SEARCH_RESULT_SET_SELECTED;

type ListingShape = HitListingShape | MapHitListingShape;

export type HitEventDataListingShape = {
    listing: ListingShape,
    hitIndex?: number,
    page?: number,
    pageSize?: number
};

export type MultiUnitPropertyDropDownShape = {
    propertyId: number
};

export type MultiUnitPropertyResultCountShape = {
    propertyId: number,
    resultCount: number
};

function parseBadges(partnerBadges: ?(Badge[])) {
    let badges;
    if (partnerBadges) {
        badges = partnerBadges.map(({id}) => id);
    }

    return (badges || []).join(',');
}

const triggerMultiUnitButtonClickEdapEvent = (
    eventData: MultiUnitPropertyDropDownShape,
    eventCategory: HitEdapEventAction,
    delayProcessing?: boolean
) => {
    const options = {
        delayProcessing
    };
    let data = {
        propertyID: eventData.propertyId
    };
    edapTrigger(eventCategory, data, options);
};

const triggerMultiUnitResultCountEdapEvent = (
    eventCategory: HitEdapEventAction,
    eventData: MultiUnitPropertyResultCountShape,
    options?: Object
) => {
    let data = {
        propertyID: eventData.propertyId,
        resultCount: eventData.resultCount
    };
    edapTrigger(eventCategory, data, options);
};

const triggerHitEdapEvent = (
    eventData: HitEventDataListingShape,
    eventCategory: HitEdapEventAction,
    delayProcessing?: boolean,
    skipReportPositioning?: boolean,
    skipPriceSummaryTrigger?: boolean,
    isHighFrequency?: boolean
) => {
    const options = {delayProcessing};
    const {
        listingId,
        partnerBadges,
        priceSummary,
        virtualTourBadge
    } = eventData.listing;
    const badges = [
        parseBadges(partnerBadges),
        virtualTourBadge && 'virtualTourBadge'
    ]
        .filter(Boolean)
        .join(',');

    let data = {
        eventlistingids: listingId,
        badges
    };

    if (!skipReportPositioning && eventData.hitIndex !== null) {
        const relativePosition = eventData.hitIndex + 1;
        let absolutePosition;
        if (eventData.page && eventData.pageSize) {
            absolutePosition =
                (eventData.page - 1) * eventData.pageSize +
                eventData.hitIndex +
                1;
        } else {
            absolutePosition = relativePosition;
        }

        data = {
            ...data,
            absoluteposition: absolutePosition.toString(),
            relativeposition: relativePosition.toString()
        };
    }
    if (isHighFrequency) {
        data = {
            ...data,
            highfrequency: 'true'
        };
    }

    edapTrigger(eventCategory, data, options);

    if (priceSummary && !skipPriceSummaryTrigger) {
        edapTriggerPriceSummaryDisplay(priceSummary, {delayProcessing});
    }
};

const triggerMultiUnitItemEdapEvent = (
    eventData: MultiUnitItemShape,
    eventCategory: typeof MULTIUNIT_ITEM_CLICK,
    delayProcessing?: boolean
) => {
    const options = {delayProcessing};

    let data = {
        unitData: eventData
    };

    edapTrigger(eventCategory, data, options);
};

const triggerSearchResultSetSelectedEdapEvent = (
    eventData: string,
    eventCategory: HitEdapEventAction,
    delayProcessing?: boolean
) => {
    const options = {delayProcessing};

    let data = {
        action_location: eventData
    };

    edapTrigger(eventCategory, data, options);
};

export const triggerMultipleUnitsClickEDAPEvent = (
    eventData: MultiUnitPropertyDropDownShape
) => {
    triggerMultiUnitButtonClickEdapEvent(
        eventData,
        MULTI_UNIT_BUTTON_CLICK,
        false
    );
};

export const triggerMultipleUnitsResultCountEDAPEvent = (
    eventData: MultiUnitPropertyResultCountShape
) => {
    triggerMultiUnitResultCountEdapEvent(MULTI_UNIT_RESULT_COUNT, eventData, {
        delayProcessing: false
    });
};

export const triggerListHitClickEDAPEvent = (
    eventData: HitEventDataListingShape
) => {
    triggerHitEdapEvent(eventData, LIST_HIT_CLICK, true, false, false);
    triggerSearchResultSetSelectedEdapEvent(
        'search',
        SEARCH_RESULT_SET_SELECTED,
        false
    );
};

export const triggerMultiUnitHitClickEDAPEvent = (
    eventData: HitEventDataListingShape
) => {
    triggerHitEdapEvent(eventData, MULTI_UNIT_HIT_CLICK, false, false, false);
};

export const triggerMultiUnitHitExposedEDAPEvent = (
    eventData: HitEventDataListingShape
) => {
    triggerHitEdapEvent(
        eventData,
        MULTI_UNIT_HIT_EXPOSED,
        false,
        false,
        false,
        true
    );
};

export const triggerMapHitClickEDAPEvent = (
    eventData: HitEventDataListingShape
) => {
    triggerHitEdapEvent(eventData, MAP_HIT_CLICK, true, false, false);
};

export const triggerMarkerClickEDAPEvent = (
    eventData: HitEventDataListingShape
) => {
    triggerHitEdapEvent(eventData, MAP_MARKER_CLICK, false, false, true);
};

export const triggerExpandedGroupHitClickEDAPEvent = (
    eventData: HitEventDataListingShape
) => {
    triggerHitEdapEvent(eventData, LIST_HIT_CLICK, true, true, false);
};

export const triggerMultiUnitItemClickEDAPEvent = (
    eventData: MultiUnitItemShape
) => {
    triggerMultiUnitItemEdapEvent(eventData, MULTIUNIT_ITEM_CLICK, false);
};
